/**
 * Axios Instance Configuration
 * 
 * SOLID Principles Applied:
 * - Single Responsibility: This file only handles HTTP client configuration
 * - Open/Closed: Easy to extend with new interceptors without modifying core logic
 * 
 * KISS Principle: Simple, centralized configuration for all API calls
 */

import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { tokenStore } from '../tokenStore';
import { toast } from 'react-hot-toast';
import { notifyUnauthorized } from '../authSession';
import { isAuthRefreshRequestUrl } from '../../utils/authHttpErrors';

// Type-safe API error response
export interface ApiErrorResponse {
  statusCode: number;
  message: string | string[];
  error: string;
  path?: string;
  timestamp: string;
}

// Create axios instance with default config
const axiosInstance: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // ✅ Send cookies with every request!
});

/**
 * Request Interceptor
 * Automatically attaches JWT token to requests if available
 */
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Get token from in-memory store (not persisted)
    const token = tokenStore.getAccessToken();
    
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor
 * Handles common error scenarios (401, 403, 500, etc.)
 * 
 * Note: This interceptor only logs errors and cleans up tokens.
 * Navigation/redirection is handled at the application level (App.tsx or route guards).
 */
axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiErrorResponse>) => {
    // Handle different HTTP error codes
    if (error.response) {
      const { status, data } = error.response;
      const url = error.config?.url || '';
      
      switch (status) {
        case 401: {
          const isAuthEndpoint = url.includes('/auth/login') || 
                                 url.includes('/auth/register') ||
                                 url.includes('/auth/refresh') ||
                                 url.includes('/auth/me');
          const isSilentGuestProbe = isAuthRefreshRequestUrl(url);

          if (!isAuthEndpoint) {
            tokenStore.clear();
            notifyUnauthorized();
          }

          if (!isSilentGuestProbe) {
            console.error('Unauthorized: Please login again');
          }
          break;
        }
          
        case 403:
          console.error('Forbidden: You don\'t have permission to access this resource');
          break;
          
        case 404:
          console.error('Resource not found:', url);
          break;
          
        case 429: {
          // Rate limit exceeded - show user-friendly message
          const retryAfter = data?.retryAfter || 60;
          toast.error(`Too many requests. Please try again in ${retryAfter} seconds.`, {
            duration: 5000,
            icon: '⏱️',
          });
          console.warn('Rate limit exceeded:', { retryAfter, url });
          break;
        }
          
        case 500:
          console.error('Server error occurred:', {
            status,
            message: data?.message || 'Internal Server Error',
            path: data?.path || url,
            timestamp: data?.timestamp || new Date().toISOString(),
            fullError: data
          });
          break;
          
        default:
          console.error(`API Error (${status}):`, data?.message || 'Unknown error');
      }
    } else if (error.request) {
      // Request was made but no response received
      console.error('Network error: No response from server');
    } else {
      // Something happened in setting up the request
      console.error('Request setup error:', error.message);
    }
    
    return Promise.reject(error);
  }
);

export default axiosInstance;


